import { useQueries } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import React from 'react';
import {
  CatalogPropertiesService,
  IntegrationConnectionStatus,
  IntegrationProviderId,
  PropertyPortal,
  type CatalogProperty,
} from 'src/api/generated';
import { useIntegrations } from 'src/modules/shared/hooks/api/integrations/integrations';
import { usePortalPublications } from 'src/modules/shared/hooks/api/integrations/portal-publications';
import { useHasPermissions } from 'src/modules/shared/hooks/auth';
import { createQuery } from 'src/modules/shared/utils/api/query-builder.util';
import { getMeliIntegrationMode } from 'src/modules/shared/utils/integrations/meli-integration.util';
import { Route as CatalogPropertiesRoute } from 'src/routes/catalog/properties.index';
import { Route as CatalogPropertiesEditRoute } from 'src/routes/catalog/properties.$propertyId.edit';
import { Route as CatalogPropertiesPublicationsRoute } from 'src/routes/catalog/properties.publications';
import {
  IntegrationPermission,
  PropertyPermission,
} from 'src/modules/shared/types/permissions.type';
import { getSupportedMeliOperationFromPublication } from '../utils/meli-publication.util';
import type { PropertiesSearch } from '../schemas/properties-search.schema';
import type {
  PropertyPublicationPortalFilter,
  PropertyPublicationsSearch,
} from '../schemas/property-publications-search.schema';

const PAGE_SIZE = 20;

export const usePropertyPublicationsPage = (params: {
  search: PropertyPublicationsSearch;
  backSearch: PropertiesSearch;
}) => {
  const navigate = useNavigate();

  const { hasPermission: canReadAll } = useHasPermissions(
    PropertyPermission.Read
  );
  const { hasPermission: canReadScoped } = useHasPermissions(
    PropertyPermission.ReadScoped
  );
  const canReadProperties = canReadAll || canReadScoped;
  const { hasPermission: canReadPublications } = useHasPermissions(
    IntegrationPermission.Read
  );
  const { integrations, loading: loadingIntegrations } =
    useIntegrations(canReadPublications);
  const [pageToken, setPageToken] = React.useState<string | undefined>(
    undefined
  );
  const [pageSize, setPageSize] = React.useState<number>(PAGE_SIZE);
  const [isToolbarExpanded, setIsToolbarExpanded] =
    React.useState<boolean>(true);
  const [managedPublicationId, setManagedPublicationId] = React.useState<
    number | null
  >(null);

  const { publicationPortal, publicationStatus } = params.search;

  const listFilterBy = React.useMemo(() => {
    const query = createQuery();
    if (publicationPortal !== 'ALL') {
      query.equals('portal', publicationPortal);
    }
    return query.build().filterBy;
  }, [publicationPortal]);

  const sortBy = React.useMemo(
    () => createQuery().orderByDesc('updatedAt').build().sortBy,
    []
  );

  const publicationsQuery = usePortalPublications({
    filterBy: listFilterBy,
    sortBy,
    displayStatus: publicationStatus,
    pageSize,
    pageToken,
    enabled: canReadPublications,
  });

  const propertyIds = React.useMemo(
    () =>
      Array.from(
        new Set(
          publicationsQuery.publications
            .map((publication) => publication.propertyId)
            .filter((propertyId): propertyId is number =>
              Number.isFinite(propertyId)
            )
        )
      ),
    [publicationsQuery.publications]
  );

  const propertyQueries = useQueries({
    queries: propertyIds.map((propertyId) => ({
      queryKey: ['catalog/properties', 'detail', propertyId],
      queryFn: async () =>
        await CatalogPropertiesService.getProperty(propertyId),
      staleTime: 60_000,
      enabled: canReadProperties,
    })),
  });

  const loadingPropertyIds = new Set(
    propertyIds.filter(
      (_, index) => canReadProperties && propertyQueries[index]?.isPending
    )
  );

  const propertyById = React.useMemo(() => {
    const entries: [number, CatalogProperty | null][] = propertyIds.map(
      (propertyId, index) => [propertyId, propertyQueries[index]?.data ?? null]
    );
    return new Map<number, CatalogProperty | null>(entries);
  }, [propertyIds, propertyQueries]);

  const meliConnection = React.useMemo(
    () =>
      integrations?.items.find(
        (item) => item.providerId === IntegrationProviderId.MELI
      )?.connection ?? null,
    [integrations]
  );
  const isMeliConnected =
    meliConnection?.status === IntegrationConnectionStatus.ACTIVE;
  const isMeliSyncMode =
    isMeliConnected &&
    getMeliIntegrationMode(meliConnection.config ?? null) === 'SYNC';

  const managedPublication = React.useMemo(
    () =>
      publicationsQuery.publications.find(
        (publication) => publication.id === managedPublicationId
      ) ?? null,
    [managedPublicationId, publicationsQuery.publications]
  );
  const managedProperty =
    managedPublication != null
      ? propertyById.get(managedPublication.propertyId) ?? null
      : null;
  const managedOperation =
    managedPublication != null
      ? getSupportedMeliOperationFromPublication(managedPublication.operation)
      : null;
  const managedPublicationsFilterBy = React.useMemo(() => {
    if (managedProperty == null) {
      return undefined;
    }
    return createQuery().equals('propertyId', managedProperty.id).build()
      .filterBy;
  }, [managedProperty]);

  const managedPropertyPublicationsQuery = usePortalPublications({
    filterBy: managedPublicationsFilterBy,
    enabled:
      managedPublication != null &&
      managedProperty != null &&
      managedPublication.portal === PropertyPortal.MELI &&
      canReadPublications,
  });

  const handleBack = React.useCallback(() => {
    void navigate({
      to: CatalogPropertiesRoute.to,
      search: {
        ...params.backSearch,
        returnTo: undefined,
      },
    });
  }, [navigate, params.backSearch]);

  const handlePublicationStatusChange = React.useCallback(
    (value: PropertyPublicationsSearch['publicationStatus']) => {
      setPageToken(undefined);
      void navigate({
        to: CatalogPropertiesPublicationsRoute.to,
        search: {
          ...params.search,
          publicationStatus: value,
        },
      });
    },
    [navigate, params.search]
  );

  const handlePublicationPortalChange = React.useCallback(
    (value: PropertyPublicationPortalFilter) => {
      setPageToken(undefined);
      void navigate({
        to: CatalogPropertiesPublicationsRoute.to,
        search: {
          ...params.search,
          publicationPortal: value,
        },
      });
    },
    [navigate, params.search]
  );

  const handleOpenProperty = React.useCallback(
    (propertyId: number) => {
      if (!canReadProperties) {
        return;
      }

      void navigate({
        to: CatalogPropertiesEditRoute.to,
        params: { propertyId },
        search: {
          ...params.backSearch,
          returnTo: 'publications',
        },
      });
    },
    [canReadProperties, navigate, params.backSearch]
  );

  const handlePageSizeChange = React.useCallback((newSize: number) => {
    setPageSize(newSize);
    setPageToken(undefined);
  }, []);

  const handleNextPage = React.useCallback(() => {
    if (publicationsQuery.nextPageToken) {
      setPageToken(publicationsQuery.nextPageToken);
    }
  }, [publicationsQuery.nextPageToken]);

  return {
    handleBack,
    handleNextPage,
    handleOpenProperty,
    handlePageSizeChange,
    handlePublicationPortalChange,
    handlePublicationStatusChange,
    isMeliConnected,
    isMeliSyncMode,
    isToolbarExpanded,
    loadingIntegrations,
    managedOperation,
    managedProperty,
    managedPropertyPublicationsQuery,
    managedPublication,
    pageSize,
    propertyById,
    loadingPropertyIds,
    publicationPortal,
    publicationStatus,
    publicationsQuery,
    setIsToolbarExpanded,
    setManagedPublicationId,
  };
};
